import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  ROUTE_META,
  XAI_DEFAULT_MODEL,
  XAI_PROVIDER,
  providerModels,
  inputClass,
  labelClass,
  buttonPrimary,
  buttonGhost,
  errorMessage,
} from "./aiRoutes";

const emptyDraft = (cfg, defaultModel) => ({
  provider: cfg.provider || XAI_PROVIDER,
  model: cfg.model || defaultModel,
  fallback_order: cfg.fallback_order || [],
  enabled_providers: cfg.enabled_providers || [cfg.provider || XAI_PROVIDER],
  temperature: cfg.temperature ?? "",
  max_tokens: cfg.max_tokens ?? 2048,
});

/** Saves route-specific provider selection and fallback order. */
export default function RouteConfigForm({ route, settings, onSaved }) {
  const cfg = settings?.routes?.[route];
  const providers = settings?.provider_status || [];
  const defaultProvider = cfg?.provider || XAI_PROVIDER;
  const defaultModel = providers.find((p) => p.provider === defaultProvider)?.default_model || XAI_DEFAULT_MODEL;
  const fallbackOrderKey = (cfg?.fallback_order || []).join(",");
  const enabledProvidersKey = (cfg?.enabled_providers || []).join(",");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (cfg) setDraft(emptyDraft(cfg, defaultModel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, cfg?.provider, cfg?.model, cfg?.temperature, cfg?.max_tokens, defaultModel,
    fallbackOrderKey, enabledProvidersKey]);

  if (!draft) return <div className="text-sm text-zinc-500">Loading…</div>;

  const meta = ROUTE_META[route];
  const models = providerModels(settings, draft.provider);
  const modelOptions = models.includes(draft.model) || !draft.model ? models : [draft.model, ...models];

  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setTestResult(null);
  };

  const payload = () => ({
    provider: draft.provider,
    model: draft.model.trim(),
    fallback_order: draft.fallback_order,
    enabled_providers: draft.enabled_providers,
    temperature: draft.temperature === "" ? null : Number(draft.temperature),
    max_tokens: Number(draft.max_tokens),
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/ai/settings/routes/${route}`, payload());
      toast.success(`${meta.title} saved`);
      await onSaved?.();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/api/ai/settings/test", {
        provider: draft.provider,
        model: draft.model.trim(),
      });
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: errorMessage(e, "Test failed") });
    } finally {
      setTesting(false);
    }
  };

  const updateProvider = (provider) => {
    const providerInfo = providers.find((p) => p.provider === provider);
    const enabled = new Set([...draft.enabled_providers, provider]);
    setDraft((current) => ({
      ...current,
      provider,
      model: providerInfo?.default_model || providerModels(settings, provider)[0] || "",
      fallback_order: current.fallback_order.filter((item) => item !== provider),
      enabled_providers: [...enabled],
    }));
    setTestResult(null);
  };

  const toggleFallback = (provider, checked) => {
    const fallback_order = checked
      ? [...draft.fallback_order, provider]
      : draft.fallback_order.filter((item) => item !== provider);
    const enabled_providers = checked
      ? [...new Set([...draft.enabled_providers, provider])]
      : draft.enabled_providers;
    update({ fallback_order, enabled_providers });
  };

  const moveFallback = (index, direction) => {
    const next = [...draft.fallback_order];
    const target = index + direction;
    [next[index], next[target]] = [next[target], next[index]];
    update({ fallback_order: next });
  };

  const toggleEnabled = (provider, checked) => {
    if (provider === draft.provider) return;
    const enabled_providers = checked
      ? [...new Set([...draft.enabled_providers, provider])]
      : draft.enabled_providers.filter((item) => item !== provider);
    update({ enabled_providers });
  };

  const primaryStatus = providers.find((p) => p.provider === draft.provider);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor={`ai-provider-${route}`}>Primary provider</label>
          <select id={`ai-provider-${route}`} className={inputClass} value={draft.provider}
            onChange={(e) => updateProvider(e.target.value)} data-testid={`ai-provider-${route}`}>
            {providers.map((provider) => <option key={provider.provider} value={provider.provider}>{provider.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor={`ai-model-${route}`}>Model</label>
          <select
            id={`ai-model-${route}`}
            className={inputClass}
            value={draft.model}
            onChange={(e) => update({ model: e.target.value })}
          >
            {modelOptions.map((m) => (
              <option key={m} value={m}>
                {m}{m === defaultModel ? " (default)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor={`ai-temp-${route}`}>Temperature (blank = model default)</label>
          <input
            id={`ai-temp-${route}`}
            type="number"
            min="0"
            max="2"
            step="0.1"
            className={inputClass}
            value={draft.temperature}
            onChange={(e) => update({ temperature: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor={`ai-max-${route}`}>Max tokens (1–8192)</label>
          <input
            id={`ai-max-${route}`}
            type="number"
            min="1"
            max="8192"
            className={inputClass}
            value={draft.max_tokens}
            onChange={(e) => update({ max_tokens: e.target.value })}
          />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className={labelClass}>Fallback order</legend>
        {draft.fallback_order.map((provider, index) => {
          const item = providers.find((p) => p.provider === provider);
          return <div key={provider} className="flex items-center gap-2 text-sm text-zinc-200">
            <span className="flex-1">{item?.label || provider}</span>
            <button type="button" className={buttonGhost} aria-label={`Move ${provider} fallback up`}
              disabled={index === 0} onClick={() => moveFallback(index, -1)}><ChevronUp size={14} /></button>
            <button type="button" className={buttonGhost} aria-label={`Move ${provider} fallback down`}
              disabled={index === draft.fallback_order.length - 1} onClick={() => moveFallback(index, 1)}><ChevronDown size={14} /></button>
          </div>;
        })}
        {providers.filter((provider) => provider.provider !== draft.provider).map((provider) => (
          <label key={provider.provider} className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={draft.fallback_order.includes(provider.provider)}
              onChange={(e) => toggleFallback(provider.provider, e.target.checked)} />
            Add {provider.label} to fallback order
          </label>
        ))}
      </fieldset>

      <fieldset className="space-y-2">
        <legend className={labelClass}>Enabled providers</legend>
        {providers.map((provider) => (
          <label key={provider.provider} className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={draft.enabled_providers.includes(provider.provider)}
              disabled={provider.provider === draft.provider}
              onChange={(e) => toggleEnabled(provider.provider, e.target.checked)} />
            {provider.label}{provider.provider === draft.provider ? " (primary)" : ""}
          </label>
        ))}
      </fieldset>

      {primaryStatus && !primaryStatus.configured && (
        <p className="text-xs text-amber-400">
          {primaryStatus.label} is not configured. Set {primaryStatus.env_key} in the server environment.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={buttonGhost} onClick={test} disabled={testing || !draft.model.trim()}>
          {testing ? "Testing…" : "Test connection"}
        </button>
        <button type="button" className={buttonPrimary} onClick={save} disabled={saving}>
          {saving ? "Saving…" : `Save ${meta.label} config`}
        </button>
      </div>

      {testResult && (
        <div
          className={`text-xs rounded px-3 py-2 border ${
            testResult.ok
              ? "border-emerald-700/50 bg-emerald-900/20 text-emerald-300"
              : "border-red-700/50 bg-red-900/20 text-red-300"
          }`}
        >
          {testResult.ok
            ? `Connected to ${testResult.model} in ${testResult.latency_ms} ms`
            : `Connection failed${testResult.http_status ? ` (HTTP ${testResult.http_status})` : ""}: ${testResult.error || testResult.error_code || "unknown error"}`}
        </div>
      )}
    </div>
  );
}
